import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { EFFICIENCY_LEVEL } from '../types';
import type { EfficiencyLevel } from '../types';

export function getEfficiencyLevel(efficiency: number): EfficiencyLevel {
  if (efficiency >= 90) return EFFICIENCY_LEVEL.OPTIMAL;
  if (efficiency >= 75) return EFFICIENCY_LEVEL.GOOD;
  if (efficiency >= 60) return EFFICIENCY_LEVEL.REGULAR;
  return EFFICIENCY_LEVEL.LOW;
}

export const LEVEL_STYLES: Record<EfficiencyLevel, string> = {
  [EFFICIENCY_LEVEL.OPTIMAL]: 'bg-green-100 text-green-700',
  [EFFICIENCY_LEVEL.GOOD]: 'bg-blue-100 text-blue-700',
  [EFFICIENCY_LEVEL.REGULAR]: 'bg-amber-100 text-amber-700',
  [EFFICIENCY_LEVEL.LOW]: 'bg-red-100 text-red-700',
};

interface PerformanceCellBadgeProps {
  efficiency: number | null;
  taskCount: number;
  className?: string;
}

export function PerformanceCellBadge({
  efficiency,
  taskCount,
  className,
}: PerformanceCellBadgeProps) {
  const { t } = useTranslation('laborPerformance');

  if (efficiency === null || taskCount === 0) {
    return (
      <div className={cn('flex flex-col items-center gap-0.5', className)}>
        <span className="text-xs font-medium text-muted-foreground">{t('timeline.naLabel')}</span>
        <span className="text-[10px] text-muted-foreground">
          {t('timeline.taskCount', { count: 0 })}
        </span>
      </div>
    );
  }

  const level = getEfficiencyLevel(efficiency);

  return (
    <div
      className={cn('flex flex-col items-center gap-0.5', className)}
      data-testid="labor-performance-cell"
    >
      <span className={cn('rounded px-1.5 py-0.5 text-xs font-semibold', LEVEL_STYLES[level])}>
        {efficiency}%
      </span>
      <span className="text-[10px] text-muted-foreground">
        {t('timeline.taskCount', { count: taskCount })}
      </span>
    </div>
  );
}
